import * as React from 'react';
import { t } from '@reos/i18n';
import { enableOfficeSite, fetchOfficeSite, type OfficeSite } from '../lib/office-site-api.js';
import { DomainApiError } from '../lib/api-error.js';

const cs = { locale: 'cs' } as const;

/**
 * Krok UI4 — Nastavení → Web kanceláře (SR6). Nárok (roční tarif) ověřuje
 * SERVER při zapnutí (`officeWebsiteEligible`) — karta jen zavolá API a
 * ukáže problem+json text kanceláři, nevymýšlí si vlastní pravidlo o tarifu.
 *
 * Výběr jednotlivých nabídek (zadání) backend nemá — web ukazuje VŠECHNY
 * zapnuté stránky nemovitosti kanceláře automaticky (SR6 `getPublicOfficeSite`),
 * viz report „## ZBÝVÁ".
 */
export function OfficeWebsiteCard(): React.JSX.Element {
  const [site, setSite] = React.useState<OfficeSite | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    fetchOfficeSite().then(
      (loadedSite) => {
        setSite(loadedSite);
        setLoaded(true);
      },
      () => setLoaded(true),
    );
  }, []);

  function enable(): void {
    setBusy(true);
    setError(null);
    void enableOfficeSite()
      .then((created) => setSite(created))
      .catch((cause: unknown) =>
        setError(
          t('webAdmin.settings.officeWebsiteError', {
            ...cs,
            values: { error: cause instanceof DomainApiError ? cause.message : String(cause) },
          }),
        ),
      )
      .finally(() => setBusy(false));
  }

  function copyLink(): void {
    if (site === null) return;
    void navigator.clipboard?.writeText(site.publicUrl).then(() => setCopied(true));
  }

  if (!loaded) return <></>;

  return (
    <section className="rh-a2-card" data-testid="settings-office-website">
      <div className="rh-a2-card__head">
        <h2>{t('webAdmin.settings.officeWebsiteTitle', cs)}</h2>
      </div>
      <p className="rh-a2-card__hint">{t('webAdmin.settings.officeWebsiteHint', cs)}</p>

      {site === null ? (
        <button
          type="button"
          className="rh-a2-btn rh-a2-btn--primary"
          disabled={busy}
          onClick={enable}
          data-testid="settings-office-website-enable"
        >
          {t('webAdmin.settings.officeWebsiteEnableButton', cs)}
        </button>
      ) : (
        <>
          <p className="rh-a2-status" data-testid="settings-office-website-status">
            {t('webAdmin.settings.officeWebsiteActiveNote', cs)}
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <a
              className="rh-a2-btn"
              href={site.publicUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="settings-office-website-preview"
            >
              {t('webAdmin.settings.officeWebsitePreviewButton', cs)}
            </a>
            <button type="button" className="rh-a2-btn" onClick={copyLink} data-testid="settings-office-website-copy">
              {t('webAdmin.settings.officeWebsiteCopyButton', cs)}
            </button>
          </div>
          <p className="rh-a2-meta" style={{ margin: '10px 0 0' }} data-testid="settings-office-website-link">
            {t('webAdmin.settings.officeWebsiteLinkLabel', cs)}: {site.publicUrl}
          </p>
          <p className="rh-a2-meta" style={{ margin: '4px 0 0' }}>
            {t('webAdmin.settings.officeWebsiteListingsNote', cs)}
          </p>
        </>
      )}

      {copied ? (
        <p role="status" className="rh-a2-meta" style={{ margin: '10px 0 0' }} data-testid="settings-office-website-copied">
          {t('webAdmin.settings.officeWebsiteCopied', cs)}
        </p>
      ) : null}
      {error !== null ? (
        <p role="alert" className="rh-a2-error" style={{ margin: '10px 0 0' }} data-testid="settings-office-website-error">
          {error}
        </p>
      ) : null}
    </section>
  );
}
